import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTag, FaCopy, FaExclamationTriangle } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { formatDate, hasSubscriptionFeature } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';

const SchoolDiscountCodes = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [discountCodes, setDiscountCodes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Check if school has permission to manage discount codes
  const canManageDiscounts = hasSubscriptionFeature(user, 'discountCodesManagement');
  
  const initialFormState = {
    code: '',
    description: '',
    discountPercentage: 10,
    type: 'ecommerce',
    applicableFor: {
      allProducts: true,
      products: [],
      minimumAmount: 0
    },
    expiresAt: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
    usageLimit: null,
    isActive: true
  };
  
  const [formData, setFormData] = useState(initialFormState);
  
  useEffect(() => {
    if (canManageDiscounts) {
      fetchDiscountCodes();
      fetchProducts();
    }
  }, [canManageDiscounts]);
  
  const fetchDiscountCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/discounts?type=ecommerce');
      setDiscountCodes(response.data.data);
    } catch (error) {
      console.error('Error fetching discount codes:', error);
      showToast('Failed to load discount codes', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProducts = async () => {
    try {
      const response = await api.get('/ecommerce/products');
      setProducts(response.data.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      showToast('Failed to load products', 'error');
    }
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const filteredDiscountCodes = discountCodes.filter(code => {
    return code.code.toLowerCase().includes(searchTerm.toLowerCase()) || 
           code.description.toLowerCase().includes(searchTerm.toLowerCase());
  });
  
  const openModal = (code = null) => {
    if (code) {
      // Edit mode - load the selected code
      const expiryDate = new Date(code.expiresAt).toISOString().split('T')[0];
      
      setFormData({
        code: code.code,
        description: code.description,
        discountPercentage: code.discountPercentage,
        type: 'ecommerce',
        applicableFor: {
          allProducts: code.applicableFor.allProducts || false,
          products: code.applicableFor.products || [],
          minimumAmount: code.applicableFor.minimumAmount || 0
        },
        expiresAt: expiryDate,
        usageLimit: code.usageLimit,
        isActive: code.isActive
      });
      
      setSelectedCode(code);
    } else {
      // Add mode - reset to initial state
      setFormData({
        ...initialFormState,
        code: generateRandomCode()
      });
      setSelectedCode(null);
    }
    setModalOpen(true);
  };
  
  const closeModal = () => {
    setModalOpen(false);
    setSelectedCode(null);
  };
  
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('applicableFor.')) {
      const field = name.split('.')[1];
      
      // Special handling for allProducts checkbox
      if (field === 'allProducts' && checked) {
        setFormData(prev => ({
          ...prev,
          applicableFor: {
            ...prev.applicableFor,
            allProducts: true,
            products: [] // Clear selected products when "All Products" is checked
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          applicableFor: {
            ...prev.applicableFor,
            [field]: type === 'checkbox' ? checked : value
          }
        }));
      }
    } else if (name === 'products') {
      // Handle multiple product selection
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData(prev => ({
        ...prev,
        applicableFor: {
          ...prev.applicableFor,
          products: selectedOptions,
          allProducts: false // Uncheck "All Products" when specific products are selected
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsFormSubmitting(true);
      
      // Format data for submission
      const submitData = {
        ...formData,
        usageLimit: formData.usageLimit === '' ? null : parseInt(formData.usageLimit),
        discountPercentage: parseInt(formData.discountPercentage)
      };
      
      if (selectedCode) {
        // Update existing discount code
        await api.put(`/discounts/${selectedCode._id}`, submitData);
        showToast('Discount code updated successfully', 'success');
      } else {
        // Create new discount code
        await api.post('/discounts', submitData);
        showToast('Discount code created successfully', 'success');
      }
      
      // Refresh the list
      fetchDiscountCodes();
      closeModal();
    } catch (error) {
      console.error('Error saving discount code:', error);
      showToast(error.response?.data?.error || 'Failed to save discount code', 'error');
    } finally {
      setIsFormSubmitting(false);
    }
  };
  
  const openDeleteConfirm = (code) => {
    setSelectedCode(code);
    setConfirmDeleteOpen(true);
  };
  
  const handleDelete = async () => {
    try {
      setIsFormSubmitting(true);
      await api.delete(`/discounts/${selectedCode._id}`);
      showToast('Discount code deleted successfully', 'success');
      
      // Refresh the list
      fetchDiscountCodes();
      setConfirmDeleteOpen(false);
    } catch (error) {
      console.error('Error deleting discount code:', error);
      showToast('Failed to delete discount code', 'error');
    } finally {
      setIsFormSubmitting(false);
    }
  };
  
  const generateRandomCode = () => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };
  
  const handleGenerateNewCode = () => {
    setFormData(prev => ({
      ...prev,
      code: generateRandomCode()
    }));
  };
  
  const copyCodeToClipboard = (code) => {
    navigator.clipboard.writeText(code).then(
      () => {
        showToast('Discount code copied to clipboard', 'success');
      },
      () => {
        showToast('Failed to copy code', 'error');
      }
    );
  };
  
  if (!canManageDiscounts) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Subscription Limitation</h3>
              <div className="text-sm text-yellow-700">
                <p>
                  Discount code management is only available for schools with Elevate subscription tier. 
                  Please upgrade your subscription to create discount codes for your products.
                </p>
                <a
                  href="/school/subscription"
                  className="font-medium underline text-yellow-700 hover:text-yellow-600 mt-2 inline-block"
                >
                  Upgrade Subscription
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Shop Discount Codes</h1>
        <button
          onClick={() => openModal()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <FaPlus className="mr-2" /> Create Discount Code
        </button>
      </div>
      
      {/* Search */}
      <div className="bg-white shadow-sm rounded-md p-4 mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search discount codes..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>
      
      {/* Discount Codes List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="text-center py-10">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredDiscountCodes.length === 0 ? (
          <div className="text-center py-10">
            <FaTag className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No discount codes found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new discount code for your shop.</p>
            <div className="mt-6">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => openModal()}
              >
                <FaPlus className="-ml-1 mr-2 h-5 w-5" />
                New Discount Code
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Code & Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Discount
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Applies To
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Usage & Expiry
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDiscountCodes.map((discountCode) => (
                  <tr key={discountCode._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="flex items-center text-sm font-medium text-gray-900">
                            {discountCode.code}
                            <button 
                              onClick={() => copyCodeToClipboard(discountCode.code)}
                              className="ml-2 text-gray-400 hover:text-gray-600"
                              title="Copy to clipboard"
                            >
                              <FaCopy />
                            </button>
                          </div>
                          <div className="text-sm text-gray-500">{discountCode.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">
                        {discountCode.discountPercentage}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {discountCode.applicableFor.allProducts ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            All Products
                          </span>
                        ) : discountCode.applicableFor.products && discountCode.applicableFor.products.length > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {discountCode.applicableFor.products.length} Products
                          </span>
                        ) : (
                          <span className="text-gray-500">Not specified</span>
                        )}
                        
                        {discountCode.applicableFor.minimumAmount > 0 && (
                          <span className="block text-xs text-gray-500 mt-1">
                            Min. KES {discountCode.applicableFor.minimumAmount}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        Used: {discountCode.usedCount} {discountCode.usageLimit ? `/ ${discountCode.usageLimit}` : ''}
                      </div>
                      <div className="text-sm text-gray-500">
                        Expires: {formatDate(discountCode.expiresAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          discountCode.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {discountCode.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openModal(discountCode)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => openDeleteConfirm(discountCode)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Add/Edit Discount Code Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={selectedCode ? "Edit Discount Code" : "Create Discount Code"}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                Code
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  type="text"
                  name="code"
                  id="code"
                  required
                  className="flex-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300"
                  value={formData.code}
                  onChange={handleFormChange}
                />
                <button
                  type="button"
                  className="ml-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={handleGenerateNewCode}
                >
                  Generate
                </button>
              </div>
            </div>
            
            <div className="sm:col-span-3">
              <label htmlFor="discountPercentage" className="block text-sm font-medium text-gray-700">
                Discount Percentage
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="number"
                  name="discountPercentage"
                  id="discountPercentage"
                  min="1"
                  max="100"
                  required
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md"
                  value={formData.discountPercentage}
                  onChange={handleFormChange}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">%</span>
                </div>
              </div>
            </div>
            
            <div className="sm:col-span-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="description"
                  id="description"
                  required
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.description}
                  onChange={handleFormChange}
                />
              </div>
            </div>
            
            <div className="sm:col-span-6">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="applicableFor.allProducts"
                    name="applicableFor.allProducts"
                    type="checkbox"
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    checked={formData.applicableFor.allProducts}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="applicableFor.allProducts" className="font-medium text-gray-700">
                    Apply to all products
                  </label>
                  <p className="text-gray-500">
                    If unchecked, you can select specific products below
                  </p>
                </div>
              </div>
            </div>
            
            {!formData.applicableFor.allProducts && (
              <div className="sm:col-span-6">
                <label htmlFor="products" className="block text-sm font-medium text-gray-700">
                  Applicable Products
                </label>
                <div className="mt-1">
                  <select
                    id="products"
                    name="products"
                    multiple
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={formData.applicableFor.products}
                    onChange={handleFormChange}
                    size={5}
                  >
                    {products.map(product => (
                      <option key={product._id} value={product._id}>
                        {product.name} (KES {product.price})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Hold Ctrl/Cmd to select multiple products
                  </p>
                </div>
              </div>
            )}
            
            <div className="sm:col-span-3">
              <label htmlFor="applicableFor.minimumAmount" className="block text-sm font-medium text-gray-700">
                Minimum Purchase Amount (KES)
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="applicableFor.minimumAmount"
                  id="applicableFor.minimumAmount"
                  min="0"
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.applicableFor.minimumAmount}
                  onChange={handleFormChange}
                />
                <p className="mt-1 text-sm text-gray-500">
                  0 means no minimum amount required
                </p>
              </div>
            </div>
            
            <div className="sm:col-span-3">
              <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700">
                Expiration Date
              </label>
              <div className="mt-1">
                <input
                  type="date"
                  name="expiresAt"
                  id="expiresAt"
                  required
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.expiresAt}
                  onChange={handleFormChange}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            
            <div className="sm:col-span-3">
              <label htmlFor="usageLimit" className="block text-sm font-medium text-gray-700">
                Usage Limit
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="usageLimit"
                  id="usageLimit"
                  min="1"
                  placeholder="Unlimited"
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.usageLimit === null ? '' : formData.usageLimit}
                  onChange={handleFormChange}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Leave empty for unlimited uses
                </p>
              </div>
            </div>
            
            <div className="sm:col-span-3">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    checked={formData.isActive}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="isActive" className="font-medium text-gray-700">
                    Active
                  </label>
                  <p className="text-gray-500">
                    Inactive codes cannot be used even if they are valid
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={closeModal}
              disabled={isFormSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={isFormSubmitting}
            >
              {isFormSubmitting ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  {selectedCode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                selectedCode ? 'Update Discount Code' : 'Create Discount Code'
              )}
            </button>
          </div>
        </form>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="Delete Discount Code"
        size="sm"
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete the discount code <span className="font-bold">{selectedCode ? selectedCode.code : ''}</span>? This action cannot be undone.
          </p>
        </div>
        
        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={isFormSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            onClick={handleDelete}
            disabled={isFormSubmitting}
          >
            {isFormSubmitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SchoolDiscountCodes; 